import type { AnnounceDetailInput as GQLInput } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * Passthrough over the generated input with one deviation: the announce form holds `tags`
 * as a comma-separated string while the schema wants `[String!]`, so that field is widened
 * here and normalized by `toGqlInput`.
 */
export type AnnounceDetailInput = Omit<GQLInput, 'tags'> & { tags?: string | string[] | null };

/**
 * Every `AnnounceDetailInput` field except `tags`, which `toGqlInput` handles separately.
 * Enumerated from the schema, deliberately not from the admin store's `SaveAnnounceParams` —
 * that type under-declares what the modal actually submits, so deriving the list from it
 * would silently stop persisting the fields it omits. A misspelling here is a compile error;
 * an omission is not, which is what `AnnounceDetailInput.spec.ts`'s round-trip test guards.
 */
const GQL_FIELDS = [
  'announce', 'announceArtists', 'announceAt', 'artists', 'attachments', 'buttonText',
  'categoryId', 'description', 'eventId', 'eventName', 'facebookUrl', 'id', 'images',
  'instagramUrl', 'onSaleAt', 'promoter', 'subcategoryId', 'support', 'ticketsUrl',
  'ticketsUrlType', 'tracking', 'triggeredAt', 'twitterUrl', 'typeId', 'websiteUrl',
] as const;

/**
 * Compile-time exhaustiveness guard. `take()` errors on a misspelled key but not on a
 * *missing* one, so without this a field added to the schema by a later codegen run would
 * silently stop persisting. This turns that into a build failure.
 */
type _AllGqlFieldsListed =
  [Exclude<keyof Omit<GQLInput, 'tags'>, (typeof GQL_FIELDS)[number]>] extends [never] ? true : never;
const _allGqlFieldsListed: _AllGqlFieldsListed = true;
void _allGqlFieldsListed;

const normalizeTags = (tags: string | string[]): string[] =>
  (Array.isArray(tags) ? tags : tags.split(',')).map(tag => tag.trim()).filter(Boolean);

export const toGqlInput = ({ tags, ...rest }: AnnounceDetailInput): GQLInput => ({
  ...take<GQLInput, GQLInput>(rest, ...GQL_FIELDS),
  // Spread conditionally: emitting `tags: []` for an input that never mentioned tags would
  // clear every tag on the announce server-side.
  ...(tags == null ? {} : { tags: normalizeTags(tags) }),
});
